import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AppContext } from '@/contexts/AppContext';
import type { OrderAdminResponse } from '@/types/api';

const ORDER_STATUSES: Array<{ value: string; label: string }> = [
  { value: 'pending', label: 'Pendente' },
  { value: 'confirmed', label: 'Confirmado' },
  { value: 'delivered', label: 'Entregue' },
  { value: 'cancelled', label: 'Cancelado' },
];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value / 100);

const formatDate = (date?: string) => {
  if (!date) return '—';
  try {
    return new Date(date).toLocaleString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    return date;
  }
};

const OrdersManagement: React.FC = () => {
  const context = useContext(AppContext);

  if (!context) {
    throw new Error('OrdersManagement deve ser usado dentro de AppProvider');
  }

  const {
    adminOrders,
    loadAdminOrders,
    confirmOrder,
    cancelOrder,
    updateOrderStatus,
  } = context;

  const [statusFilter, setStatusFilter] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<OrderAdminResponse | null>(null);

  const currentPage = adminOrders?.current_page ?? 1;
  const lastPage = adminOrders?.last_page ?? 1;

  const orders = adminOrders?.data ?? [];

  const statusLookup = useMemo(() =>
    ORDER_STATUSES.reduce<Record<string, string>>((acc, status) => {
      acc[status.value] = status.label;
      return acc;
    }, {}), []);

  const fetchOrders = async (page = 1) => {
    setLoading(true);
    setError(null);
    setFeedback(null);
    try {
      const pagination = await loadAdminOrders({
        page,
        status: statusFilter || undefined,
      });
      if (pagination.data.length === 0) {
        setFeedback('Nenhum pedido encontrado para o filtro selecionado.');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar pedidos.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchOrders(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleConfirm = async (orderId: number) => {
    setLoading(true);
    setError(null);
    setFeedback(null);
    try {
      await confirmOrder(orderId);
      setFeedback('Pedido confirmado com sucesso.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao confirmar pedido.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (orderId: number) => {
    const confirmation = window.confirm('Tem certeza que deseja cancelar este pedido?');
    if (!confirmation) return;

    setLoading(true);
    setError(null);
    setFeedback(null);
    try {
      await cancelOrder(orderId);
      setFeedback('Pedido cancelado com sucesso.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao cancelar pedido.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId: number, status: string) => {
    setLoading(true);
    setError(null);
    setFeedback(null);
    try {
      await updateOrderStatus(orderId, status);
      setFeedback('Status do pedido atualizado.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao atualizar status do pedido.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (order: OrderAdminResponse) => {
    setSelectedOrder(order);
  };

  const closeDetails = () => {
    setSelectedOrder(null);
  };

  const handleChangePage = (page: number) => {
    if (page < 1 || page > lastPage || page === currentPage) return;
    void fetchOrders(page);
  };

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>Gerenciamento de Pedidos</h2>
        <div className="section-actions">
          <select
            className="status-select"
            value={statusFilter}
            onChange={event => setStatusFilter(event.target.value)}
            disabled={loading}
          >
            <option value="">Todos os status</option>
            {ORDER_STATUSES.map(status => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => fetchOrders(currentPage)}
            disabled={loading}
          >
            Recarregar
          </button>
        </div>
      </div>

      {error && <p className="error">{error}</p>}
      {feedback && <p className="success">{feedback}</p>}

      {orders.length === 0 ? (
        <p>{loading ? 'Carregando pedidos...' : 'Nenhum pedido encontrado.'}</p>
      ) : (
        <table className="admin-table admin-orders-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Cliente</th>
              <th>Total</th>
              <th>Status</th>
              <th>Data</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {orders.map(order => (
              <tr key={order.id}>
                <td>{order.id}</td>
                <td>{order.customer_name ?? order.client?.name ?? '—'}</td>
                <td>{formatCurrency(order.total_cents)}</td>
                <td>
                  <span className={`order-status ${order.status ?? 'pending'}`}>
                    {statusLookup[order.status ?? 'pending'] ?? order.status ?? '—'}
                  </span>
                </td>
                <td>{formatDate(order.created_at)}</td>
                <td className="actions">
                  <div className="admin-actions">
                    <button
                      type="button"
                      className="icon-button icon-button--details"
                      onClick={() => handleViewDetails(order)}
                      disabled={loading}
                      title="Ver detalhes do pedido"
                    >
                      🔍
                      <span className="sr-only">Detalhes</span>
                    </button>
                    <button
                      type="button"
                      className="icon-button icon-button--confirm"
                      onClick={() => handleConfirm(order.id)}
                      disabled={loading}
                      title="Confirmar pedido"
                    >
                      ✅
                      <span className="sr-only">Confirmar</span>
                    </button>
                    <button
                      type="button"
                      className="icon-button icon-button--cancel"
                      onClick={() => handleCancel(order.id)}
                      disabled={loading}
                      title="Cancelar pedido"
                    >
                      ✖
                      <span className="sr-only">Cancelar</span>
                    </button>
                    <select
                      className={`status-select status-${order.status ?? 'pending'}`}
                      value={order.status ?? 'pending'}
                      onChange={event => handleStatusChange(order.id, event.target.value)}
                      disabled={loading}
                    >
                      {ORDER_STATUSES.map(status => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {lastPage > 1 && (
        <div className="pagination">
          <button type="button" onClick={() => handleChangePage(currentPage - 1)} disabled={loading || currentPage === 1}>
            Anterior
          </button>
          <span>
            Página {currentPage} de {lastPage}
          </span>
          <button type="button" onClick={() => handleChangePage(currentPage + 1)} disabled={loading || currentPage === lastPage}>
            Próxima
          </button>
        </div>
      )}

      {selectedOrder && (
        <div className="admin-modal">
          <div className="modal-content">
            <h3>Pedido #{selectedOrder.id}</h3>
            <p>
              <strong>Cliente:</strong> {selectedOrder.customer_name ?? selectedOrder.client?.name ?? '—'}
            </p>
            <p>
              <strong>Contato:</strong> {selectedOrder.customer_email ?? selectedOrder.client?.email ?? '—'} |{' '}
              {selectedOrder.customer_phone ?? selectedOrder.client?.phone ?? '—'}
            </p>
            <p>
              <strong>Total:</strong> {formatCurrency(selectedOrder.total_cents)}
            </p>
            <p>
              <strong>Status:</strong> {statusLookup[selectedOrder.status ?? 'pending'] ?? selectedOrder.status ?? '—'}
            </p>
            <p>
              <strong>Criado em:</strong> {formatDate(selectedOrder.created_at)}
            </p>

            <h4>Itens</h4>
            {selectedOrder.items?.length ? (
              <ul>
                {selectedOrder.items.map(item => (
                  <li key={item.id}>
                    {item.product_name ?? `Produto #${item.product_id}`} - {item.quantity}x ({formatCurrency(item.total_cents)})
                  </li>
                ))}
              </ul>
            ) : (
              <p>Nenhum item cadastrado.</p>
            )}

            {selectedOrder.status_history && selectedOrder.status_history.length > 0 && (
              <div>
                <h4>Histórico de status</h4>
                <ul>
                  {selectedOrder.status_history.map(history => (
                    <li key={history.changed_at}>
                      {formatDate(history.changed_at)} - {statusLookup[history.status] ?? history.status}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={closeDetails}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersManagement;
